/**
 * RoomToken - 외부 API를 통해 채팅방에 메시지를 보내기 위한 토큰
 *
 * Firestore 경로: roomtokens/{tokenId} (루트 컬렉션)
 *
 * @note secretHash는 SHA-256 해시로 저장. 평문 키는 생성 시 한 번만 표시됨.
 * @note 토큰 형식: npt_ + 32자 랜덤 hex (총 36자)
 * @note roomId 필드로 소속 방을 참조. 인덱스를 통해 O(1) 조회 가능.
 */
export class RoomToken {
  static COL = 'roomtokens';

  id: string;
  name: string;                         // 표시 이름 (예: "GitHub Bot")
  photoUrl: string | null = null;       // 프로필 사진 URL
  secretHash: string;                   // SHA-256 해시된 시크릿 키
  roomId: string;                       // 소속 방 ID
  createdBy: string;                    // 생성자 UID
  created: Date = new Date();
  isActive: boolean = true;             // 활성/비활성 (삭제 없이 일시 중지)
  // 권한 스코프
  scopes: string[] = ['message:send'];  // 'message:send' | 'files:read' | 'memos:read'
  // 파일/메모 공개 범위 ('all' = 전체, 'selected' = 선택한 항목만)
  filesVisibility: 'all' | 'selected' = 'all';
  memosVisibility: 'all' | 'selected' = 'all';
  visibleFileIds: string[] = [];        // selected 모드일 때 공개할 파일 ID
  visibleMemoIds: string[] = [];        // selected 모드일 때 공개할 메모 ID

  // 보안: IP 허용 목록 (빈 배열이면 제한 없음)
  allowedIps: string[] = [];

  // 사용량 제한 (Quota)
  monthlyQuota: number = 10000;         // 월간 최대 호출 수 (기본: 10,000)
  currentMonthUsage: number = 0;        // 현재 월 누적 사용량 (캐시)
  lastUsageDate?: string;               // 마지막 사용일 "YYYY-MM-DD" (달 전환 판단용)

  constructor(name: string, roomId: string, createdBy: string, secretHash: string) {
    this.id = uuid();
    this.name = name;
    this.roomId = roomId;
    this.createdBy = createdBy;
    this.secretHash = secretHash;
  }

  /**
   * 새 토큰 시크릿 키 생성 (npt_ 접두사 + 32자 hex)
   * @returns 평문 키 (한 번만 표시, 저장하지 않음)
   */
  static generateSecret(): string {
    const hex = 'xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx'.replace(/x/g, () => {
      return (Math.random() * 16 | 0).toString(16);
    });
    return `npt_${hex}`;
  }

  /**
   * 시크릿 키를 SHA-256 해시로 변환
   * 브라우저 환경에서는 Web Crypto API 사용
   */
  static async hashSecret(secret: string): Promise<string> {
    const encoder = new TextEncoder();
    const data = encoder.encode(secret);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  }
}

function uuid(): string {
  return 'xxxxxxxxxxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    // eslint-disable-next-line
    var r = Math.random() * 16 | 0, v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
}
